import { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { IconClock } from './Icons';

const STEP_MINUTES = 15;

function parseTime24(value: string): { hour: number; minute: number } {
  const raw = (value ?? '').trim();
  if (!raw || !raw.includes(':')) return { hour: 0, minute: 0 };
  const [h, m] = raw.split(':').map(Number);
  return { hour: Number.isFinite(h) ? h : 0, minute: Number.isFinite(m) ? m : 0 };
}

function toTimeString(hour24: number, minute: number): string {
  return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/** Normalises any `H:m` input to a padded 24h `HH:mm` label. */
function formatTime24h(value: string): string {
  const { hour, minute } = parseTime24(value);
  return toTimeString(hour, minute);
}

/** All `HH:mm` slots for a day at STEP_MINUTES intervals (00:00 … 23:45). */
function buildSlots(): string[] {
  const slots: string[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += STEP_MINUTES) {
    slots.push(toTimeString(Math.floor(minutes / 60), minutes % 60));
  }
  return slots;
}

const BASE_SLOTS = buildSlots();

export interface TimePickerProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  disabled?: boolean;
}

/** Offers-module time picker: dropdown of 15-minute slots. Value / onChange use `HH:mm` 24h. */
export function TimePicker({ label, value, onChange, error, disabled = false }: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [panelBox, setPanelBox] = useState({ top: 0, left: 0, width: 260 });
  const [hovered, setHovered] = useState<string | null>(null);

  const normalized = useMemo(() => {
    if (!value) return '';
    const { hour, minute } = parseTime24(value);
    return toTimeString(hour, minute);
  }, [value]);

  /** Off-grid legacy values (e.g. 10:07) stay selectable so nothing is silently lost. */
  const slots = useMemo(() => {
    if (!normalized || BASE_SLOTS.includes(normalized)) return BASE_SLOTS;
    return [...BASE_SLOTS, normalized].sort();
  }, [normalized]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const handleSelect = (slot: string) => {
    onChange(slot);
    setOpen(false);
  };

  const updatePanelPosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const w = Math.max(r.width, 200);
    let left = r.left;
    if (left + w > window.innerWidth - margin) left = window.innerWidth - w - margin;
    left = Math.max(margin, left);
    const estH = 280;
    let top = r.bottom + 6;
    if (top + estH > window.innerHeight - margin) top = Math.max(margin, r.top - estH - 6);
    setPanelBox({ top, left, width: w });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Bring the current selection into view when the dropdown opens.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      const selected = listRef.current?.querySelector('[data-selected="true"]') as HTMLElement | null;
      selected?.scrollIntoView({ block: 'center' });
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Keep the panel glued to the trigger while the page (or any scroll container) moves.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    const reposition = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updatePanelPosition();
      });
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (wrapperRef.current?.contains(t)) return;
      if (t.closest('[data-offer-time-popover]')) return;
      setOpen(false);
    };
    const id = window.setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const displayLabel = value ? formatTime24h(value) : '--:--';

  const popover = open && (
    <div
      data-offer-time-popover
      role="listbox"
      aria-label={label}
      style={{
        position: 'fixed',
        top: panelBox.top,
        left: panelBox.left,
        width: panelBox.width,
        zIndex: 10300,
        background: 'var(--surface, #FFFFFF)',
        borderRadius: 'var(--radius-card, 14px)',
        boxShadow: 'var(--card-shadow, 0 8px 32px rgba(0,0,0,0.14))',
        border: '1px solid var(--card-border, #E2E5EC)',
        minWidth: 180,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div ref={listRef} style={{ maxHeight: 264, overflowY: 'auto', padding: '6px 0' }}>
        {slots.map((slot) => {
          const isSelected = slot === normalized;
          const isHovered = slot === hovered;
          return (
            <button
              key={slot}
              type="button"
              role="option"
              aria-selected={isSelected}
              data-selected={isSelected}
              onClick={() => handleSelect(slot)}
              onMouseEnter={() => setHovered(slot)}
              onMouseLeave={() => setHovered((prev) => (prev === slot ? null : prev))}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '9px 16px',
                border: 'none',
                background: isSelected
                  ? 'var(--accent-soft, rgba(210,58,59,0.08))'
                  : isHovered
                    ? 'var(--hover, #F0F1F5)'
                    : 'transparent',
                color: isSelected ? 'var(--accent, #D23A3B)' : 'var(--t1, #1A1D2E)',
                fontFamily: 'var(--font-mono, monospace)',
                fontSize: 13,
                fontWeight: isSelected ? 700 : 500,
                cursor: 'pointer',
                transition: 'background 0.12s ease',
              }}
            >
              {formatTime24h(slot)}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }} ref={wrapperRef}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--t2, #5A6178)', fontFamily: 'var(--font-primary)', lineHeight: '16px' }}>
        {label}
      </label>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '10px 14px',
          borderRadius: 'var(--radius-input, 8px)',
          border: error ? '1px solid var(--red, #E5484D)' : '1px solid var(--card-border, #E2E5EC)',
          fontFamily: 'var(--font-primary)',
          fontSize: 13,
          color: value ? 'var(--t1, #1A1D2E)' : 'var(--t3, #9CA3B8)',
          background: disabled ? 'var(--hover, #F0F1F5)' : 'var(--surface, #FFFFFF)',
          minHeight: 40,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <IconClock size={16} style={{ color: 'var(--t3, #9CA3B8)', flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{displayLabel}</span>
        <span
          aria-hidden="true"
          style={{
            color: 'var(--t3, #9CA3B8)',
            fontSize: 10,
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
            lineHeight: 1,
          }}
        >
          ▼
        </span>
      </button>
      {error && <p style={{ fontSize: 11, fontWeight: 500, color: 'var(--red, #E5484D)', margin: 0 }}>{error}</p>}

      {typeof document !== 'undefined' && open && createPortal(popover, document.body)}
    </div>
  );
}
